import Vue from 'vue'
import Component from 'vue-class-component'
import { ProductApiModel } from '@/interfaces/ProductApiModel'
import { OptionApiModel } from '@/interfaces/OptionApiModel'
import lodash from 'lodash'
import { namespace } from 'vuex-class'
import { OptionRestrictionApiModel } from '@/interfaces/OptionRestrictionApiModel'
import { OptionCategoryApiModel } from '@/interfaces/OptionCategoryApiModel'
import { useOptionStore } from '@/store/modules/options'
import { SubOptionType } from '@/interfaces/SubOptionType'
import { OptionRestrictionBehavior } from '@/enums/OptionRestrictionBehavior'

const currentModel = namespace('currentModel')

@Component
export class OptionValidator extends Vue {
  @currentModel.State
  public model!: ProductApiModel

  public optionStore = useOptionStore();

  public disableOption(optionItem: OptionApiModel): boolean {
    return this.disableOptionWithModel(optionItem, this.model)
  }

  public hideOption(optionItem: OptionApiModel): boolean {
    return optionItem.isHidden || this.hideOptionWithModel(optionItem, this.model)
  }

  private hideOptionWithModel(optionItem: OptionApiModel, dataModel: ProductApiModel): boolean {
    const appliedRestrictions = this.findAppliedRestrictions(optionItem, dataModel)
    return appliedRestrictions.some(r => r.restrictionBehavior === OptionRestrictionBehavior.HideOption)
  }

  public disableOptionWithModel(optionItem: OptionApiModel, dataModel: ProductApiModel): boolean {
    if (optionItem) {
      const appliedRestrictions = this.findAppliedRestrictions(optionItem, dataModel)
      // console.log('disableCheck', appliedRestrictions)
      return appliedRestrictions.some(r =>
        r.restrictionBehavior === OptionRestrictionBehavior.DisableWithDescription
      )
    }
    return true
  }

  public disableOrHideOptionWithModel(optionItem: OptionApiModel, dataModel: ProductApiModel): boolean {
    return this.disableOptionWithModel(optionItem, dataModel) || this.hideOptionWithModel(optionItem, dataModel)
  }

  public optionTooltipDescription(optionItem: OptionApiModel): string | undefined {
    return this.optionTooltipDescriptionWithModel(optionItem, this.model)
  }

  private optionTooltipDescriptionWithModel(optionItem: OptionApiModel, dataModel: ProductApiModel): string | undefined {
    const restrictions = this.findAppliedRestrictions(optionItem, dataModel)
      .filter(r => r.description && (
        r.restrictionBehavior === OptionRestrictionBehavior.DisableWithDescription ||
        r.restrictionBehavior === OptionRestrictionBehavior.OnlyShowDescription
      ))

    if (restrictions.length > 0) {
      return restrictions.map(r => '<div>' + r.description + '</div>').join('')
    }

    return undefined
  }

  private findAppliedRestrictions(optionItem: OptionApiModel, dataModel: ProductApiModel, recurseTested?: string[]): OptionRestrictionApiModel[] {
    recurseTested = recurseTested ?? []

    let restrictions = [...optionItem.restrictions]

    const is60 = dataModel.configurator.width?.toString() === '60'
    const is72 = dataModel.configurator.width?.toString() === '72'

    if ((optionItem.code === 'MSR1' && is60) ||
      (optionItem.code === 'MSR2' && is72)) {
      let sinkOffsetRestriction = restrictions.find(r => r.property === 'sinkOffset')

      restrictions = restrictions.filter(r => r.property !== 'width')
      restrictions = restrictions.filter(r => r.property !== 'sinkOffset')

      if (sinkOffsetRestriction) {
        sinkOffsetRestriction = Object.assign({}, sinkOffsetRestriction)
        if (is60) {
          sinkOffsetRestriction.value = '14.625'
        } else if (is72) {
          sinkOffsetRestriction.value = '17.625'
        }
        restrictions.push(sinkOffsetRestriction)
      }

      restrictions.push({ property: 'doubleSink', id: 0, description: undefined, value: '1', operator: 'notEqual', restrictionBehavior: OptionRestrictionBehavior.HideOption })
    }

    if (optionItem && !recurseTested.includes(optionItem.code)) {
      recurseTested.push(optionItem.code)

      // Check parent restrictions first
      const parentRestrictions = restrictions.filter(r => !r.parentRestrictionId)

      const appliedRestrictions = parentRestrictions.filter(r => this.evaluateComplexRestriction(r, restrictions, dataModel))

      if (appliedRestrictions.length) {
        return appliedRestrictions
      }

      if ((optionItem.requiredOptions?.length ?? 0) > 0) {
        const requiredOptions = this.optionStore.allOptions.filter(o => optionItem.requiredOptions?.includes(o.code))
        const applied = requiredOptions.flatMap(o => this.findAppliedRestrictions(o, dataModel, recurseTested))
        return applied.filter(apply => apply !== undefined)
      }
    }

    return []
  }

  private evaluateComplexRestriction(parentRestriction: OptionRestrictionApiModel, allRestrictions: OptionRestrictionApiModel[], dataModel: ProductApiModel): boolean {
    // First check if the parent restriction itself applies
    const parentApplies = this.restrictionApplies(parentRestriction, dataModel)

    // Get child restrictions for this parent
    const childRestrictions = allRestrictions.filter(r => r.parentRestrictionId === parentRestriction.id)

    if (childRestrictions.length === 0) {
      // No child restrictions, just return parent result
      return parentApplies
    }

    // Group child restrictions by logical operator
    const andRestrictions = childRestrictions.filter(r => r.logicalOperator === 'AND')
    const orRestrictions = childRestrictions.filter(r => r.logicalOperator === 'OR')

    let result = parentApplies

    // Process AND restrictions - all must be true
    if (andRestrictions.length > 0) {
      const andResults = andRestrictions.map(r => this.restrictionApplies(r, dataModel))
      result = result && andResults.every(r => r)
    }

    // Process OR restrictions - at least one must be true
    if (orRestrictions.length > 0) {
      const orResults = orRestrictions.map(r => this.restrictionApplies(r, dataModel))
      result = result || orResults.some(r => r)
    }

    return result
  }

  private restrictionApplies(restriction: OptionRestrictionApiModel, dataModel: ProductApiModel): boolean {
    let sourceModelValue = ''
    let convertibleToNumber = true

    switch (restriction.property) {
      case 'productCode':
        sourceModelValue = dataModel.code
        break
      case 'productSizeItemNumber':
        sourceModelValue = dataModel.sizeItemNumber
        break
      case 'configurationSize':
        sourceModelValue = dataModel.configurator.configuration + dataModel.configurator.width.toString()
        break
      case 'widthminusdoublesinkoffset':
        sourceModelValue = (dataModel.configurator.width - Math.abs((+dataModel.configurator.sinkOffset) * 2)).toString()
        break
      case 'width':
        sourceModelValue = dataModel.configurator.width.toString()
        break
      case 'configuration':
        sourceModelValue = dataModel.configurator.configuration
        convertibleToNumber = false
        break
      default:
        sourceModelValue = lodash.get(dataModel.configurator, restriction.property)
        break
    }

    if (restriction.value?.includes('||')) {
      const restrictionValues = restriction.value?.toString().split('||') ?? []

      let applies = false

      // console.log('OR (any must apply)', restrictionValues)

      restrictionValues.forEach(restrictionValue => {
        const thisApplies = this.checkRestriction(restriction.operator, restrictionValue, sourceModelValue, convertibleToNumber)
        // console.log(restriction.id + ': "' + restriction.property + ' ' + sourceModelValue + '" ' + restriction.operator + ' ' + restrictionValue + ' applies: ' + thisApplies)
        applies = applies || thisApplies
      })
      return applies
    } else {
      const restrictionValues = restriction.value?.toString().split('&&') ?? []

      let applies = restrictionValues.length > 0

      // console.log('AND (all must apply)', restrictionValues)

      restrictionValues.forEach(restrictionValue => {
        const thisApplies = this.checkRestriction(restriction.operator, restrictionValue, sourceModelValue, convertibleToNumber)
        // console.log(restriction.id + ': "' + restriction.property + ' ' + sourceModelValue + '" ' + restriction.operator + ' ' + restrictionValue + ' applies: ' + thisApplies)
        applies = applies && thisApplies
      })
      return applies
    }
  }

  private checkRestriction(operator: string, sourceRestrictionValue: string, sourceModelValue: string, convertToNumber = true): boolean {
    let restrictionValue
    let modelValue

    if (!isNaN(+sourceRestrictionValue) && !isNaN(+sourceModelValue) && convertToNumber) {
      restrictionValue = +sourceRestrictionValue
      modelValue = +sourceModelValue
    } else {
      restrictionValue = sourceRestrictionValue
      modelValue = sourceModelValue
    }

    switch (operator) {
      case 'equal':
        return restrictionValue === modelValue
      case 'notEqual':
        return restrictionValue !== modelValue
      case 'lowerThan':
        return modelValue < restrictionValue
      case 'lowerEqualThan':
        return modelValue <= restrictionValue
      case 'higherThan':
        return modelValue > restrictionValue
      case 'higherEqualThan':
        return modelValue >= restrictionValue
      case 'startsWith':
        return sourceModelValue.startsWith(sourceRestrictionValue)
      case 'contains':
        return sourceModelValue.indexOf(sourceRestrictionValue) > -1
      case 'doesntStartsWith':
        return !sourceModelValue.startsWith(sourceRestrictionValue)
      case 'doesntContains':
        return sourceModelValue.indexOf(sourceRestrictionValue) === -1
    }
    return false
  }

  public hideCategory(category: OptionCategoryApiModel): boolean {
    return this.hideCategoryWithModel(category, this.model)
  }

  private hideCategoryWithModel(category: OptionCategoryApiModel, dataModel: ProductApiModel): boolean {
    const validOption = lodash.find(category.options, (categoryOption) => {
      return !this.hideOptionWithModel(categoryOption, dataModel)
    })

    const hasHiddenSubOptionType = category.subOptionTypes.some(type => this.hideSubOptionTypeWithModel(type, dataModel))
    return validOption === undefined || hasHiddenSubOptionType
  }

  private hideSubOptionTypeWithModel(type: SubOptionType, dataModel: ProductApiModel) {
    const validSubOption = lodash.find(type.subOptions, (subOption) => {
      return !this.hideOptionWithModel(subOption, dataModel)
    })

    return validSubOption === undefined
  }
}
